/**
 * The rows every way into New request draws — `docs/new-request.md` §13.
 *
 * A list's split menu, the nav's **Create** menu, a record's ⋯ and the phone
 * sheets all offer *New request* with the same sub-line and the same **N R**,
 * so the words and the look are written once here.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { Keys, PlusGlyph } from '../components/keycap.js'

/** *contact, property, job and a site visit — one sheet* (§3). */
export const NEW_REQUEST_SUB = 'contact, property, job and a site visit — one sheet'
export const NEW_REQUEST_KEYS = ['N', 'R'] as const

/**
 * One row of a desktop menu: a 17px glyph, a 13/600 title over an 11.5 muted
 * sub-line, and keys on the right. `feature` is the request row — the
 * green-band fill and the accent title (Board 1 A); `tile` is the nav's Create
 * menu, whose title is bold and whose + is accent-dark (Board 1 D).
 */
export function MenuRow({
  icon, title, sub, keys, feature, onClick, tile,
}: {
  icon?: ReactNode
  title: string
  sub?: string
  keys?: readonly string[]
  feature?: boolean
  /** A 30px tinted tile instead of the bare glyph (the nav's Create menu). */
  tile?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'mx-1 flex w-[calc(100%-8px)] items-center gap-[11px] rounded-[9px] px-3 py-[9px] text-left',
        feature ? 'bg-green-band-bg hover:bg-accent-soft' : 'hover:bg-hover',
      )}
    >
      {tile
        ? (
          <span className={cn('inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center',
            'rounded-tile-small border border-accent-line bg-accent-soft text-accent-dark')}>
            <PlusGlyph size={15} stroke={1.9} />
          </span>
        )
        : (
          <span className={cn('inline-flex shrink-0', feature ? 'text-accent' : 'text-text-muted')}>
            {icon ?? <PlusGlyph size={17} stroke={2} />}
          </span>
        )}
      {/* Normal line height, as the boards set it: the title and its sub-line sit tight. */}
      <span className="flex min-w-0 flex-col leading-[normal]">
        <b className={cn('text-[13px]', tile ? 'font-bold' : 'font-semibold',
          feature ? 'text-accent-dark' : 'text-text')}>
          {title}
        </b>
        {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
      </span>
      <span className="flex-1" />
      {keys && <Keys keys={keys} />}
    </button>
  )
}

/**
 * One row of a phone bottom sheet: 56px, a 34px tinted tile, a 15px title and
 * a 12px sub (Board 1 G).
 */
export function SheetRow({
  icon, title, sub, tone, onClick, first, tall,
}: {
  icon: ReactNode
  title: string
  sub: string
  /** `plain` is Board 16 D's grey tile beside *Create, book later*. */
  tone: 'info' | 'accent' | 'plain'
  onClick: () => void
  first?: boolean
  /** 60px, Board 16 D's two rows. */
  tall?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex w-full items-center gap-3 px-[18px] text-left', tall ? 'h-[60px]' : 'h-14',
        !first && 'border-t border-line-faint')}
    >
      <span className={cn(
        'inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-tile border',
        tone === 'accent'
          ? 'border-accent-line bg-accent-soft text-accent-dark'
          : tone === 'plain' ? 'border-line bg-line-soft text-text-muted'
            : 'border-info/25 bg-info-bg text-info',
      )}>
        {icon}
      </span>
      <span className="flex min-w-0 flex-col leading-[normal]">
        <b className="text-[15px] font-bold text-text">{title}</b>
        <span className="truncate text-[12px] text-text-muted">{sub}</span>
      </span>
    </button>
  )
}
